// src/components/OrganigrammeRH.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Award,
  BarChart3,
  BookOpen,
  CheckCircle,
  DollarSign,
  Download,
  Eye,
  FileCheck,
  FileText,
  GraduationCap,
  Handshake,
  Network,
  UserCheck,
  Users,
  Wallet,
  X,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import useOrganigrammeParams from "../hooks/useOrganigrammeParams";

const ICONS = {
  Users,
  Award,
  BarChart3,
  DollarSign,
  GraduationCap,
  BookOpen,
  UserCheck,
  FileText,
  Wallet,
  Handshake,
};

const hasTextContent = (doc) => Boolean(doc?.contenu?.trim());
const hasAttachedFile = (doc) => Boolean(doc?.fichier_url);

const getDisplayName = (item) => {
  if (item.employe?.nom_complet) return item.employe.nom_complet;
  if (item.nom_affiche) return item.nom_affiche;
  if (item.type === "groupe") return item.titre;
  return "Non renseigne";
};

const OrgAvatar = ({ item, size = "md" }) => {
  const Icon = ICONS[item.icone] || Users;
  const sizeClass = size === "lg" ? "w-14 h-14" : "w-11 h-11";
  const iconSize = size === "lg" ? 24 : 19;

  return (
    <div className={`${sizeClass} bg-white rounded-full flex items-center justify-center overflow-hidden flex-shrink-0`}>
      {item.employe?.photo_url ? (
        <img src={item.employe.photo_url} alt="" className="w-full h-full object-cover" />
      ) : (
        <Icon size={iconSize} className="text-gray-500" />
      )}
    </div>
  );
};

const OrgCard = ({ item, variant = "normal" }) => {
  const isPrimary = variant === "primary";
  const widthClass = isPrimary ? "w-72" : "w-56";
  const paddingClass = isPrimary ? "px-6 py-4" : "px-4 py-3";
  const titleClass = isPrimary ? "text-sm" : "text-xs";
  const nameClass = isPrimary ? "text-base font-bold" : "text-sm font-semibold";

  return (
    <div className="flex flex-col items-center">
      <div
        className={`flex items-center gap-3 bg-gradient-to-r ${item.couleur_fond || "from-[#246990] to-[#3084b5]"} text-white ${paddingClass} rounded-lg shadow-md ${widthClass}`}
      >
        <OrgAvatar item={item} size={isPrimary ? "lg" : "md"} />


        <div className="text-left min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`${titleClass} font-medium leading-tight truncate`}>
              {item.titre}
            </div>
            {item.type === "externe" && item.dans_hierarchie && (
              <span className="text-[10px] bg-white/20 border border-white/30 px-1.5 py-0.5 rounded-full flex-shrink-0">
                Externe
              </span>
            )}
          </div>
          <div className={`${nameClass} truncate`}>
            {getDisplayName(item)}
          </div>
        </div>


      </div>

      {item.description && (
        <div className="text-xs text-gray-600 mt-1 text-center px-2 py-1 bg-gray-50 rounded max-w-56">
          {item.description}
        </div>
      )}
    </div>
  );
};

const ExternalCard = ({ item }) => {
  const Icon = ICONS[item.icone] || Users;

  return (
    <div className="flex items-center gap-3 bg-white p-3 rounded-lg shadow-sm border border-amber-200">
      <div className={`w-12 h-12 bg-gradient-to-br ${item.couleur_fond || "from-amber-400 to-amber-500"} rounded-full flex items-center justify-center overflow-hidden flex-shrink-0`}>
        {item.employe?.photo_url ? (
          <img src={item.employe.photo_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <Icon size={20} className="text-white" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-gray-900 truncate">{getDisplayName(item)}</div>
        <div className="text-xs text-gray-600 truncate">{item.titre}</div>
      </div>
    </div>
  );
};

const ReglementCard = ({
  reglementActif,
  loadingReglement,
  onRead,
  onDownload,
  onOpenFile,
  busy,
}) => {
  if (loadingReglement) {
    return (
      <div className="flex items-center gap-2 bg-white border border-gray-300 rounded px-3 py-2 shadow-sm">
        <FileCheck size={16} className="text-[#246990] flex-shrink-0" />
        <span className="text-xs font-medium text-gray-500 flex-1">Chargement du reglement...</span>
      </div>
    );
  }

  if (!reglementActif) {
    return (
      <div className="flex items-center gap-2 bg-white border border-gray-300 rounded px-3 py-2 shadow-sm">
        <FileCheck size={16} className="text-gray-400 flex-shrink-0" />
        <span className="text-xs font-medium text-gray-500 flex-1">Aucun reglement interieur</span>
      </div>
    );
  }

  const hasText = hasTextContent(reglementActif);
  const hasFile = hasAttachedFile(reglementActif);

  return (
    <div className="bg-white border border-gray-300 rounded shadow-sm p-3">
      <div className="flex items-start gap-2">
        <FileCheck size={17} className="text-[#246990] flex-shrink-0 mt-0.5" />

        <div className="min-w-0 flex-1">
          <span className="block text-xs font-semibold text-gray-800 truncate">
            {reglementActif.titre || "Reglement interieur"}
          </span>
          {reglementActif.version && (
            <span className="block text-[11px] text-gray-500 truncate">
              Version {reglementActif.version}
            </span>
          )}
          <div className="mt-2 flex flex-wrap gap-1">
            {hasText && (
              <span className="text-[10px] bg-blue-50 text-[#246990] border border-blue-100 px-2 py-0.5 rounded-full">
                Texte
              </span>
            )}
            {hasFile && (
              <span className="text-[10px] bg-gray-50 text-gray-600 border border-gray-200 px-2 py-0.5 rounded-full">
                Fichier
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 flex justify-end gap-1">
        <button
          type="button"
          className="p-1.5 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
          onClick={() => onRead(reglementActif)}
          disabled={busy}
          title="Consulter"
        >
          <Eye size={16} className="text-[#246990]" />
        </button>

        <button
          type="button"
          className="p-1.5 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
          onClick={() => onDownload(reglementActif)}
          disabled={busy}
          title={hasText ? "Telecharger le PDF" : "Telecharger le fichier"}
        >
          <Download size={16} className="text-[#246990]" />
        </button>

        {hasFile && (
          <button
            type="button"
            className="p-1.5 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
            onClick={() => onOpenFile(reglementActif)}
            disabled={busy}
            title="Ouvrir le fichier joint"
          >
            <FileText size={16} className="text-[#246990]" />
          </button>
        )}
      </div>
    </div>
  );
};

const ReglementTextModal = ({ document, onClose, onDownloadPdf, onOpenFile }) => {
  if (!document) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="border-b px-5 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-semibold text-[#246990] truncate">
              {document.titre || "Reglement interieur"}
            </h3>
            <p className="text-xs text-gray-500">
              {document.version ? `Version ${document.version}` : "Sans version"}
              {document.nom_fichier_original ? ` - Fichier joint : ${document.nom_fichier_original}` : ""}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
            title="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          {document.description && (
            <div className="mb-4 border-l-4 border-[#246990] bg-blue-50 px-4 py-3 text-sm text-gray-700">
              {document.description}
            </div>
          )}

          <div className="whitespace-pre-wrap text-sm leading-7 text-gray-800">
            {document.contenu?.trim() || "Aucun contenu redige pour ce reglement."}
          </div>
        </div>

        <div className="border-t px-5 py-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onDownloadPdf(document)}
            className="inline-flex items-center gap-2 bg-[#246990] text-white rounded-lg px-4 py-2 text-sm hover:bg-[#1a4d6e]"
          >
            <Download size={16} />
            PDF
          </button>

          {hasAttachedFile(document) && (
            <button
              type="button"
              onClick={() => onOpenFile(document)}
              className="inline-flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-2 text-sm hover:bg-gray-50"
            >
              <FileText size={16} />
              Fichier joint
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const FilePreviewModal = ({ filePreview, onClose, onDownloadFile }) => {
  if (!filePreview) return null;

  const canPreviewPdf = filePreview.mimeType?.includes("pdf");

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden">
        <div className="border-b px-5 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-semibold text-[#246990] truncate">
              {filePreview.filename || filePreview.document?.nom_fichier_original || "Fichier joint"}
            </h3>
            <p className="text-xs text-gray-500">{filePreview.document?.titre}</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
            title="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 bg-gray-100 min-h-0">
          {canPreviewPdf ? (
            <iframe
              src={filePreview.url}
              title={filePreview.filename || "Fichier joint"}
              className="w-full h-full border-0"
            />
          ) : (
            <div className="h-full flex items-center justify-center p-6">
              <div className="bg-white border border-gray-200 rounded-lg p-8 max-w-md text-center">
                <FileText size={42} className="mx-auto text-[#246990] mb-3" />
                <p className="font-semibold text-gray-800 mb-2">Apercu indisponible</p>
                <p className="text-sm text-gray-600 mb-4">
                  Ce format de fichier ne peut pas etre affiche directement dans le navigateur.
                </p>
                <button
                  type="button"
                  onClick={() => onDownloadFile(filePreview.document)}
                  className="inline-flex items-center gap-2 bg-[#246990] text-white rounded-lg px-4 py-2 text-sm hover:bg-[#1a4d6e]"
                >
                  <Download size={16} />
                  Telecharger le fichier
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EmptyOrganigramme = () => (
  <div className="min-h-[360px] flex items-center justify-center border border-dashed border-gray-300 rounded-lg bg-gray-50">
    <div className="text-center max-w-sm px-6">
      <Network size={48} className="mx-auto text-gray-300 mb-3" />
      <p className="font-semibold text-gray-700 mb-1">Organigramme non configure</p>
      <p className="text-sm text-gray-500">
        Ajoute les postes depuis Parametres &gt; Organigramme pour afficher la structure RH.
      </p>
    </div>
  </div>
);

const OrganigrammeRH = () => {
  const {
    organigramme,
    organigrammeByLevel,
    reglementActif,
    loading,
    loadingReglement,
    error,
    downloadReglementPdf,
    downloadOrganigrammePdf,
    downloadReglementFile,
    fetchReglementFilePreview,
  } = useOrganigrammeParams();

  const [previewReglement, setPreviewReglement] = useState(null);
  const [filePreview, setFilePreview] = useState(null);

  const [actionError, setActionError] = useState("");
  const [busyReglement, setBusyReglement] = useState(false);
  const [downloadingOrganigrammePdf, setDownloadingOrganigrammePdf] = useState(false);
  const [organigrammePdfError, setOrganigrammePdfError] = useState("");

  useEffect(() => {
    return () => {
      if (filePreview?.url) {
        URL.revokeObjectURL(filePreview.url);
      }
    };
  }, [filePreview?.url]);

  const levels = useMemo(() => {
    return Object.keys(organigrammeByLevel || {})
      .map(Number)
      .sort((a, b) => a - b);
  }, [organigrammeByLevel]);

  const activeItems = useMemo(() => {
    return (organigramme || []).filter((item) => item.actif);
  }, [organigramme]);


  const externalItems = useMemo(() => {
    return activeItems.filter((item) => item.type === "externe" && !item.dans_hierarchie);
  }, [activeItems]);


  const internalLevels = useMemo(() => {
    const result = {};

    levels.forEach((level) => {

      const items = (organigrammeByLevel[String(level)] || organigrammeByLevel[level] || [])
        .filter((item) => item.actif && (item.type !== "externe" || item.dans_hierarchie));

      if (items.length > 0) {
        result[level] = items;
      }
    });

    return result;
  }, [levels, organigrammeByLevel]);

  const internalLevelKeys = Object.keys(internalLevels)
    .map(Number)
    .sort((a, b) => a - b);

  const handleDownloadOrganigrammePdf = async () => {
    setOrganigrammePdfError("");
    setDownloadingOrganigrammePdf(true);

    const result = await downloadOrganigrammePdf();

    setDownloadingOrganigrammePdf(false);

    if (!result.success) {
      setOrganigrammePdfError(result.error || "Erreur lors du telechargement de l'organigramme.");
    }
  };


  const handleOpenFile = async (doc) => {
    if (!hasAttachedFile(doc)) {
      setActionError("Aucun fichier joint pour ce reglement.");
      return;
    }

    setActionError("");
    setBusyReglement(true);

    const result = await fetchReglementFilePreview(doc.id);

    setBusyReglement(false);

    if (!result.success) {
      setActionError(result.error || "Erreur lors de l'ouverture du fichier joint.");
      return;
    }

    setFilePreview({
      ...result,
      document: doc,
    });
  };

  const handleReadReglement = async (doc) => {
    setActionError("");

    if (hasTextContent(doc)) {
      setPreviewReglement(doc);
      return;
    }

    if (hasAttachedFile(doc)) {
      await handleOpenFile(doc);
      return;
    }

    setActionError("Aucun contenu disponible pour ce reglement.");
  };

  const handleDownloadPdf = async (doc) => {
    setActionError("");
    setBusyReglement(true);

    const result = await downloadReglementPdf(doc.id);

    setBusyReglement(false);

    if (!result.success) {
      setActionError(result.error || "Erreur lors du telechargement du PDF.");
    }
  };

  const handleDownloadFile = async (doc) => {
    if (!hasAttachedFile(doc)) {
      setActionError("Aucun fichier joint pour ce reglement.");
      return;
    }

    setActionError("");
    setBusyReglement(true);

    const result = await downloadReglementFile(doc.id);

    setBusyReglement(false);

    if (!result.success) {
      setActionError(result.error || "Erreur lors du telechargement du fichier joint.");
    }
  };

  const handleDownloadReglement = async (doc) => {
    if (hasTextContent(doc)) {
      await handleDownloadPdf(doc);
      return;
    }

    await handleDownloadFile(doc);
  };

  return (
    <>
      <Card className="mb-6">

        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="text-[#246990] font-bold">Organigramme</CardTitle>
              <CardDescription className="font-semibold">
                Structure hierarchique de l'etablissement
              </CardDescription>
            </div>

            <button
              type="button"
              onClick={handleDownloadOrganigrammePdf}
              disabled={downloadingOrganigrammePdf || loading || activeItems.length === 0}
              className="inline-flex items-center gap-2 border border-[#246990] text-[#246990] rounded-lg px-3 py-2 text-sm font-medium hover:bg-[#246990] hover:text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Telecharger l'organigramme en PDF"
            >
              <Download size={16} />
              {downloadingOrganigrammePdf ? "Generation..." : "PDF"}
            </button>
          </div>

          {organigrammePdfError && (
            <div className="mt-3 border border-red-200 bg-red-50 text-red-700 rounded px-3 py-2 text-xs">
              {organigrammePdfError}
            </div>
          )}
        </CardHeader>


        <CardContent className="p-8 overflow-x-auto">
          {loading && (
            <div className="min-h-[360px] flex items-center justify-center">
              <div className="text-center">
                <div className="w-10 h-10 border-4 border-[#246990] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-gray-500">Chargement de l'organigramme...</p>
              </div>
            </div>
          )}

          {!loading && error && (
            <div className="min-h-[260px] flex items-center justify-center border border-red-200 bg-red-50 rounded-lg">
              <div className="text-center text-red-700">
                <p className="font-semibold mb-1">Erreur de chargement</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && activeItems.length === 0 && <EmptyOrganigramme />}

          {!loading && !error && activeItems.length > 0 && (
            <div className="flex gap-6">
              <div className="flex-1 flex flex-col items-center space-y-6 min-w-[980px]">
                {internalLevelKeys.map((level, levelIndex) => {
                  const items = internalLevels[level] || [];
                  const isPrimaryLevel = items.length === 1 && levelIndex > 0;

                  return (
                    <React.Fragment key={level}>
                      {levelIndex > 0 && <div className="w-px h-8 bg-gray-400" />}

                      {levelIndex > 1 && (
                        <div className="relative w-full max-w-5xl h-px bg-gray-400">
                          <div className="absolute left-1/2 transform -translate-x-1/2 w-px h-8 -top-8 bg-gray-400" />
                        </div>
                      )}

                      <div className="flex items-start justify-center gap-3 flex-wrap">
                        {items.map((item) => (
                          <OrgCard
                            key={item.id}
                            item={item}
                            variant={isPrimaryLevel ? "primary" : "normal"}
                          />
                        ))}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>

              <div className="w-80 flex-shrink-0 space-y-3">
                <ReglementCard
                  reglementActif={reglementActif}
                  loadingReglement={loadingReglement}
                  onRead={handleReadReglement}
                  onDownload={handleDownloadReglement}
                  onOpenFile={handleOpenFile}
                  busy={busyReglement}
                />

                {actionError && (
                  <div className="border border-red-200 bg-red-50 text-red-700 rounded px-3 py-2 text-xs">
                    {actionError}
                  </div>
                )}

                <section className="border-2 border-dashed border-gray-300 bg-gray-50 rounded-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-4 py-3">
                    <h3 className="text-base font-semibold flex items-center gap-2">
                      <Handshake size={18} />
                      Intervenants Externes
                    </h3>
                    <p className="text-white/80 text-xs">
                      Consultants & Prestataires
                    </p>
                  </div>

                  <div className="p-4 space-y-3">
                    {externalItems.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Users size={36} className="mx-auto mb-2 text-gray-300" />
                        <p className="text-sm font-medium">Aucun intervenant externe</p>
                      </div>
                    ) : (
                      externalItems.map((item) => <ExternalCard key={item.id} item={item} />)
                    )}

                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-gray-700">
                      <p className="font-medium text-amber-800 mb-1 flex items-center gap-1">
                        <CheckCircle size={13} />
                        Intervenants externes
                      </p>
                      <p>
                        Personnes exterieures a l'etablissement intervenant pour des missions specifiques.
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ReglementTextModal
        document={previewReglement}
        onClose={() => setPreviewReglement(null)}
        onDownloadPdf={handleDownloadPdf}
        onOpenFile={handleOpenFile}
      />

      <FilePreviewModal
        filePreview={filePreview}
        onClose={() => setFilePreview(null)}
        onDownloadFile={handleDownloadFile}
      />
    </>
  );
};

export default OrganigrammeRH;